/**
 * Transaction service layer — thin wrappers around Supabase RPC calls.
 *
 * Each function validates inputs, delegates to the corresponding PostgreSQL
 * stored function via supabase.rpc(), and returns a structured result.
 * Errors are caught and returned as { error: message } rather than thrown.
 *
 * Requirements: 1.1, 2.3, 3.1, 4.1, 5.1, 6.2, 7.1, 8.3, 8.5, 8.6
 */

import { getSupabase } from './supabase.js';
import {
  validateFlightPayload,
  validateDefectPayload,
  validateMaintenancePayload,
  validateBillingPeriod,
  validateBillingTransition,
  validateImportPayload,
} from '../utils/validation.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ── Flight logging ──────────────────────────────────────────────────────────

/**
 * Log a flight atomically (flight + fuel/oil + aircraft hours).
 * @param {object} payload - Flight and fuel/oil fields.
 * @returns {{ flight_id: string, fuel_oil_id: string } | { error: string }}
 */
export async function logFlight(payload) {
  const validation = validateFlightPayload(payload);
  if (!validation.valid) {
    return { error: validation.errors.join('; ') };
  }

  try {
    const supabase = getSupabase();
    const { data, error } = await supabase.rpc('rpc_log_flight', {
      p_flight_date: payload.date,
      p_aircraft_id: payload.aircraft_id,
      p_pilot_id: payload.pilot_id,
      p_departure: payload.departure ?? null,
      p_destination: payload.destination ?? null,
      p_block_time_mins: payload.block_time_mins,
      p_flight_time_mins: payload.flight_time_mins,
      p_notes: payload.notes ?? null,
      p_main_tank_level: payload.main_tank_level ?? null,
      p_left_wing_level: payload.left_wing_level ?? null,
      p_right_wing_level: payload.right_wing_level ?? null,
      p_belly_tank_level: payload.belly_tank_level ?? null,
      p_fuel_uplift_litres: payload.fuel_uplift_litres ?? null,
      p_fuel_uplift_cost: payload.fuel_uplift_cost ?? null,
      p_oil_level: payload.oil_level ?? null,
      p_oil_unit: payload.oil_unit ?? null,
    });

    if (error) return { error: error.message };
    return data;
  } catch (err) {
    return { error: err.message };
  }
}

// ── Flight deletion ─────────────────────────────────────────────────────────

/**
 * Delete a flight and reverse aircraft hours atomically.
 * @param {string} flightId - UUID of the flight to delete.
 * @returns {{ success: true } | { error: string }}
 */
export async function deleteFlight(flightId) {
  if (!UUID_RE.test(flightId)) {
    return { error: 'flightId must be a valid UUID' };
  }

  try {
    const supabase = getSupabase();
    const { error } = await supabase.rpc('rpc_delete_flight', {
      p_flight_id: flightId,
    });

    if (error) return { error: error.message };
    return { success: true };
  } catch (err) {
    return { error: err.message };
  }
}

// ── Defect reporting ────────────────────────────────────────────────────────

/**
 * Report a defect atomically (defect record + aircraft UNSERVICEABLE).
 * @param {object} payload - { aircraft_id, description, flight_id? }
 * @returns {{ defect_id: string } | { error: string }}
 */
export async function reportDefect(payload) {
  const validation = validateDefectPayload(payload);
  if (!validation.valid) {
    return { error: validation.errors.join('; ') };
  }

  try {
    const supabase = getSupabase();
    const { data, error } = await supabase.rpc('rpc_report_defect', {
      p_aircraft_id: payload.aircraft_id,
      p_description: payload.description,
      p_flight_id: payload.flight_id ?? null,
    });

    if (error) return { error: error.message };
    return data;
  } catch (err) {
    return { error: err.message };
  }
}

// ── Defect closure ──────────────────────────────────────────────────────────

/**
 * Close a defect and conditionally mark aircraft SERVICEABLE.
 * @param {string} defectId - UUID of the defect to close.
 * @param {string} [resolution] - Resolution description.
 * @returns {{ defect_id: string, aircraft_status: string } | { error: string }}
 */
export async function closeDefect(defectId, resolution) {
  if (!UUID_RE.test(defectId)) {
    return { error: 'defectId must be a valid UUID' };
  }

  try {
    const supabase = getSupabase();
    const { data, error } = await supabase.rpc('rpc_close_defect', {
      p_defect_id: defectId,
      p_resolution: resolution ?? null,
    });

    if (error) return { error: error.message };
    return data;
  } catch (err) {
    return { error: err.message };
  }
}

// ── Maintenance logging ─────────────────────────────────────────────────────

/**
 * Log a maintenance event and reset aircraft counters atomically.
 * @param {object} payload - { aircraft_id, type, performed_date, description }
 * @returns {{ maintenance_event_id: string } | { error: string }}
 */
export async function logMaintenance(payload) {
  const validation = validateMaintenancePayload(payload);
  if (!validation.valid) {
    return { error: validation.errors.join('; ') };
  }

  try {
    const supabase = getSupabase();
    const { data, error } = await supabase.rpc('rpc_log_maintenance', {
      p_aircraft_id: payload.aircraft_id,
      p_type: payload.type,
      p_performed_at: payload.performed_date,
      p_description: payload.description ?? null,
    });

    if (error) return { error: error.message };
    return data;
  } catch (err) {
    return { error: err.message };
  }
}

// ── Billing calculation ─────────────────────────────────────────────────────

/**
 * Calculate billing for a period (snapshot flights + rates → DRAFT records).
 * @param {string} periodStart - YYYY-MM-DD
 * @param {string} periodEnd - YYYY-MM-DD
 * @returns {{ records: object[], warnings: object[] } | { error: string }}
 */
export async function calculateBilling(periodStart, periodEnd) {
  const validation = validateBillingPeriod(periodStart, periodEnd);
  if (!validation.valid) {
    return { error: validation.errors.join('; ') };
  }

  try {
    const supabase = getSupabase();
    const { data, error } = await supabase.rpc('rpc_calculate_billing', {
      p_period_start: periodStart,
      p_period_end: periodEnd,
    });

    if (error) return { error: error.message };
    return data;
  } catch (err) {
    return { error: err.message };
  }
}

// ── Billing transition ──────────────────────────────────────────────────────

/**
 * Allowed invoice status transitions.
 * DRAFT → ISSUED, ISSUED → PAID, UNBILLED → ISSUED.
 */
const ALLOWED_STATUS_TRANSITIONS = {
  DRAFT: 'ISSUED',
  UNBILLED: 'ISSUED',
  ISSUED: 'PAID',
};

/**
 * Validate an invoice status transition.
 * Returns { valid: true } for allowed transitions, or { valid: false, error: '...' } for invalid ones.
 *
 * Allowed: DRAFT→ISSUED, UNBILLED→ISSUED, ISSUED→PAID.
 *
 * @param {string} currentStatus - The current invoice status.
 * @param {string} newStatus - The target status.
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateStatusTransition(currentStatus, newStatus) {
  if (!currentStatus || !newStatus) {
    return { valid: false, error: 'Both currentStatus and newStatus are required.' };
  }

  const current = String(currentStatus).toUpperCase();
  const target = String(newStatus).toUpperCase();

  const allowed = ALLOWED_STATUS_TRANSITIONS[current];
  if (allowed === target) {
    return { valid: true };
  }

  return {
    valid: false,
    error: `Invalid transition: cannot move from ${current} to ${target}. Allowed: DRAFT→ISSUED, ISSUED→PAID.`,
  };
}

/**
 * Transition a billing record's status (DRAFT→ISSUED→PAID).
 * Fetches the current status, validates the transition client-side,
 * records transition/payment dates, then delegates to the server RPC.
 *
 * @param {string} recordId - UUID of the billing record.
 * @param {string} newStatus - Target status.
 * @returns {{ record_id: string, old_status: string, new_status: string } | { error: string }}
 */
export async function transitionBilling(recordId, newStatus) {
  if (!UUID_RE.test(recordId)) {
    return { error: 'recordId must be a valid UUID' };
  }

  const knownStatuses = ['DRAFT', 'ISSUED', 'PAID', 'UNBILLED'];
  if (!knownStatuses.includes(newStatus)) {
    return { error: `newStatus must be one of: ${knownStatuses.join(', ')}` };
  }

  try {
    const supabase = getSupabase();

    // Fetch the current status of the record to validate the transition.
    // billing_status lives on the flights table (the flight IS the bill).
    const { data: flight, error: fetchError } = await supabase
      .from('flights')
      .select('billing_status')
      .eq('id', recordId)
      .single();

    if (fetchError) return { error: fetchError.message };

    const currentStatus = flight.billing_status || 'UNBILLED';

    // Validate the transition client-side
    const validation = validateStatusTransition(currentStatus, newStatus);
    if (!validation.valid) {
      return { error: validation.error };
    }

    // Build additional fields based on the transition
    const extraUpdates = {};
    if (newStatus === 'ISSUED') {
      extraUpdates.issued_at = new Date().toISOString();
    }
    if (newStatus === 'PAID') {
      extraUpdates.paid_at = new Date().toISOString();
    }

    // Perform the transition via RPC, falling back to direct update if RPC is unavailable
    try {
      const { data, error } = await supabase.rpc('rpc_transition_billing', {
        p_record_id: recordId,
        p_new_status: newStatus,
      });

      if (error) {
        // If the RPC doesn't exist, fall back to a direct update
        if (error.message && error.message.includes('rpc_transition_billing')) {
          const { data: updated, error: updateError } = await supabase
            .from('flights')
            .update({ billing_status: newStatus, ...extraUpdates, updated_at: new Date().toISOString() })
            .eq('id', recordId)
            .select()
            .single();
          if (updateError) return { error: updateError.message };
          return { record_id: recordId, old_status: currentStatus, new_status: newStatus };
        }
        return { error: error.message };
      }
      return data;
    } catch (rpcErr) {
      // Fallback: direct update
      const { data: updated, error: updateError } = await supabase
        .from('flights')
        .update({ billing_status: newStatus, ...extraUpdates, updated_at: new Date().toISOString() })
        .eq('id', recordId)
        .select()
        .single();
      if (updateError) return { error: updateError.message };
      return { record_id: recordId, old_status: currentStatus, new_status: newStatus };
    }
  } catch (err) {
    return { error: err.message };
  }
}

// ── Flight import (single) ──────────────────────────────────────────────────

/**
 * Import a single historical flight record.
 * @param {object} payload - Import fields (aircraft_reg, pilot_name, date, etc.)
 * @returns {{ result: 'imported'|'skipped'|'failed', flight_id?: string, warnings?: string[] } | { error: string }}
 */
export async function importFlight(payload) {
  const validation = validateImportPayload(payload);
  if (!validation.valid) {
    return { error: validation.errors.join('; ') };
  }

  try {
    const supabase = getSupabase();
    const { data, error } = await supabase.rpc('rpc_import_flight', {
      p_aircraft_reg: payload.aircraft_reg,
      p_pilot_name: payload.pilot_name,
      p_flight_date: payload.date,
      p_departure: payload.departure ?? null,
      p_destination: payload.destination ?? null,
      p_block_time_mins: payload.block_time_mins,
      p_flight_time_mins: payload.flight_time_mins ?? null,
      p_notes: payload.notes ?? null,
      p_main_tank_level: payload.main_tank_level ?? null,
      p_left_wing_level: payload.left_wing_level ?? null,
      p_right_wing_level: payload.right_wing_level ?? null,
      p_belly_tank_level: payload.belly_tank_level ?? null,
      p_fuel_uplift_litres: payload.fuel_uplift_litres ?? null,
      p_fuel_uplift_cost: payload.fuel_uplift_cost ?? null,
      p_oil_level: payload.oil_level ?? null,
      p_oil_unit: payload.oil_unit ?? null,
    });

    if (error) return { error: error.message };
    return data;
  } catch (err) {
    return { error: err.message };
  }
}

// ── Batch flight import ─────────────────────────────────────────────────────

/**
 * Import a batch of historical flight records.
 * Each record is imported individually so one failure doesn't block the rest.
 *
 * @param {object[]} records - Array of import payloads.
 * @returns {{ imported: number, skipped: number, failed: number, placeholderEntities: string[], errors: Array<{ index: number, error: string }> }}
 */
export async function importFlightBatch(records) {
  if (!Array.isArray(records)) {
    return { error: 'records must be an array' };
  }

  const summary = {
    total: records.length,
    imported: 0,
    skipped: 0,
    failed: 0,
    placeholderEntities: [],
    errors: [],
  };

  for (let i = 0; i < records.length; i++) {
    const result = await importFlight(records[i]);

    if (result.error) {
      summary.failed++;
      summary.errors.push({ index: i, error: result.error });
      continue;
    }

    if (result.result === 'imported') {
      summary.imported++;
    } else if (result.result === 'skipped') {
      summary.skipped++;
    } else if (result.result === 'failed') {
      summary.failed++;
      summary.errors.push({ index: i, error: result.error || 'RPC returned failed' });
    }

    // Collect placeholder entity warnings
    if (result.warnings && Array.isArray(result.warnings)) {
      for (const warning of result.warnings) {
        if (typeof warning === 'string' && warning.startsWith('Created placeholder')) {
          if (!summary.placeholderEntities.includes(warning)) {
            summary.placeholderEntities.push(warning);
          }
        }
      }
    }
  }

  return summary;
}

// ── Component hours management ──────────────────────────────────────────────

/**
 * Increment airframe_hours, engine_hours, propeller_hours, and
 * hours_since_last_50hr for an aircraft after a flight is logged.
 *
 * @param {string} aircraftId - UUID of the aircraft.
 * @param {number} flightTimeMins - Flight time in minutes.
 * @returns {{ success: true } | { error: string }}
 *
 * Requirements: 22.4
 */
export async function incrementComponentHours(aircraftId, flightTimeMins) {
  if (!UUID_RE.test(aircraftId)) {
    return { error: 'aircraftId must be a valid UUID' };
  }
  if (typeof flightTimeMins !== 'number' || flightTimeMins < 0) {
    return { error: 'flightTimeMins must be a non-negative number' };
  }

  const hours = flightTimeMins / 60;

  try {
    const supabase = getSupabase();

    // Fetch current values
    const { data: aircraft, error: fetchError } = await supabase
      .from('aircraft')
      .select('airframe_hours, engine_hours, propeller_hours, hours_since_last_50hr')
      .eq('id', aircraftId)
      .single();

    if (fetchError) return { error: fetchError.message };

    const { error: updateError } = await supabase
      .from('aircraft')
      .update({
        airframe_hours: aircraft.airframe_hours + hours,
        engine_hours: aircraft.engine_hours + hours,
        propeller_hours: aircraft.propeller_hours + hours,
        hours_since_last_50hr: aircraft.hours_since_last_50hr + hours,
        total_hours: aircraft.airframe_hours + hours, // backward compat: total_hours = airframe_hours
        updated_at: new Date().toISOString(),
      })
      .eq('id', aircraftId);

    if (updateError) return { error: updateError.message };
    return { success: true };
  } catch (err) {
    return { error: err.message };
  }
}

/**
 * Decrement airframe_hours, engine_hours, propeller_hours, and
 * hours_since_last_50hr for an aircraft when a flight is deleted.
 *
 * @param {string} aircraftId - UUID of the aircraft.
 * @param {number} flightTimeMins - Flight time in minutes.
 * @returns {{ success: true } | { error: string }}
 *
 * Requirements: 22.5
 */
export async function decrementComponentHours(aircraftId, flightTimeMins) {
  if (!UUID_RE.test(aircraftId)) {
    return { error: 'aircraftId must be a valid UUID' };
  }
  if (typeof flightTimeMins !== 'number' || flightTimeMins < 0) {
    return { error: 'flightTimeMins must be a non-negative number' };
  }

  const hours = flightTimeMins / 60;

  try {
    const supabase = getSupabase();

    // Fetch current values
    const { data: aircraft, error: fetchError } = await supabase
      .from('aircraft')
      .select('airframe_hours, engine_hours, propeller_hours, hours_since_last_50hr')
      .eq('id', aircraftId)
      .single();

    if (fetchError) return { error: fetchError.message };

    const { error: updateError } = await supabase
      .from('aircraft')
      .update({
        airframe_hours: aircraft.airframe_hours - hours,
        engine_hours: aircraft.engine_hours - hours,
        propeller_hours: aircraft.propeller_hours - hours,
        hours_since_last_50hr: aircraft.hours_since_last_50hr - hours,
        total_hours: aircraft.airframe_hours - hours, // backward compat: total_hours = airframe_hours
        updated_at: new Date().toISOString(),
      })
      .eq('id', aircraftId);

    if (updateError) return { error: updateError.message };
    return { success: true };
  } catch (err) {
    return { error: err.message };
  }
}

// ── Consolidated flight logging ─────────────────────────────────────────────

/**
 * Atomically create a flight record, fuel/oil record, and defect records.
 * On any failure, previously created records are deleted (manual rollback).
 * Also increments component hours on the aircraft.
 *
 * @param {object} payload - { flight: {...}, fuelOil: {...}, defects: [{...}] }
 * @returns {{ flight: object, fuelOil: object|null, defects: object[] } | { error: string }}
 *
 * Requirements: 26.9, 26.10
 */
export async function logFlightWithFuelAndDefects(payload) {
  if (!payload || !payload.flight) {
    return { error: 'payload.flight is required' };
  }

  const { flight, fuelOil, defects = [], expenses = [] } = payload;
  const supabase = getSupabase();

  // Determine mode: update existing booking or create new flight
  const isCompletingBooking = !!flight.id;

  let createdFlight = null;
  let createdFuelOil = null;
  const createdDefects = [];
  const createdExpenses = [];

  try {
    // Step 1: Create or update flight record
    if (isCompletingBooking) {
      // Update existing booking row with flight-log fields + set status to FLOWN
      const { id: flightId, ...flightUpdates } = flight;
      flightUpdates.status = 'FLOWN';
      flightUpdates.updated_at = new Date().toISOString();
      const { data: flightData, error: flightError } = await supabase
        .from('flights')
        .update(flightUpdates)
        .eq('id', flightId)
        .select()
        .single();

      if (flightError) {
        return { error: `Flight update failed: ${flightError.message}` };
      }
      createdFlight = flightData;
    } else {
      // New standalone flight — ensure it has status FLOWN and booking times
      const newFlight = {
        ...flight,
        status: 'FLOWN',
        start_time: flight.start_time || flight.departure_time || '00:00',
        end_time: flight.end_time || flight.arrival_time || '23:59',
      };
      const { data: flightData, error: flightError } = await supabase
        .from('flights')
        .insert(newFlight)
        .select()
        .single();

      if (flightError) {
        return { error: `Flight creation failed: ${flightError.message}` };
      }
      createdFlight = flightData;
    }

    // Step 2: Create fuel/oil record (if provided)
    if (fuelOil) {
      const fuelOilRecord = { ...fuelOil, flight_id: createdFlight.id };
      const { data: fuelData, error: fuelError } = await supabase
        .from('flight_fuel_oil')
        .insert(fuelOilRecord)
        .select()
        .single();

      if (fuelError) {
        // Rollback flight
        if (isCompletingBooking) {
          await supabase.from('flights').update({ status: 'APPROVED' }).eq('id', createdFlight.id);
        } else {
          await supabase.from('flights').delete().eq('id', createdFlight.id);
        }
        return { error: `Fuel/oil creation failed: ${fuelError.message}` };
      }
      createdFuelOil = fuelData;
    }

    // Step 3: Create defect records
    for (const defect of defects) {
      const defectRecord = { ...defect, flight_id: createdFlight.id };
      const { data: defectData, error: defectError } = await supabase
        .from('defects')
        .insert(defectRecord)
        .select()
        .single();

      if (defectError) {
        // Rollback: delete all created defects, fuel/oil, and flight
        for (const d of createdDefects) {
          await supabase.from('defects').delete().eq('id', d.id);
        }
        if (createdFuelOil) {
          await supabase.from('flight_fuel_oil').delete().eq('id', createdFuelOil.id);
        }
        if (isCompletingBooking) {
          await supabase.from('flights').update({ status: 'APPROVED' }).eq('id', createdFlight.id);
        } else {
          await supabase.from('flights').delete().eq('id', createdFlight.id);
        }
        return { error: `Defect creation failed: ${defectError.message}` };
      }
      createdDefects.push(defectData);
    }

    // Step 4: Create expense records (if provided)
    for (const expense of expenses) {
      const expenseRecord = {
        flight_id: createdFlight.id,
        description: expense.description,
        amount: expense.amount,
      };
      const { data: expenseData, error: expenseError } = await supabase
        .from('flight_expenses')
        .insert(expenseRecord)
        .select()
        .single();

      if (expenseError) {
        // Rollback: delete all created expenses, defects, fuel/oil, and flight
        for (const e of createdExpenses) {
          await supabase.from('flight_expenses').delete().eq('id', e.id);
        }
        for (const d of createdDefects) {
          await supabase.from('defects').delete().eq('id', d.id);
        }
        if (createdFuelOil) {
          await supabase.from('flight_fuel_oil').delete().eq('id', createdFuelOil.id);
        }
        if (isCompletingBooking) {
          await supabase.from('flights').update({ status: 'APPROVED' }).eq('id', createdFlight.id);
        } else {
          await supabase.from('flights').delete().eq('id', createdFlight.id);
        }
        return { error: `Expense creation failed: ${expenseError.message}` };
      }
      createdExpenses.push(expenseData);
    }

    // Step 5: Increment component hours
    if (createdFlight.flight_time_mins) {
      const hoursResult = await incrementComponentHours(
        createdFlight.aircraft_id,
        createdFlight.flight_time_mins,
      );
      if (hoursResult.error) {
        // Rollback everything
        for (const e of createdExpenses) {
          await supabase.from('flight_expenses').delete().eq('id', e.id);
        }
        for (const d of createdDefects) {
          await supabase.from('defects').delete().eq('id', d.id);
        }
        if (createdFuelOil) {
          await supabase.from('flight_fuel_oil').delete().eq('id', createdFuelOil.id);
        }
        if (isCompletingBooking) {
          await supabase.from('flights').update({ status: 'APPROVED' }).eq('id', createdFlight.id);
        } else {
          await supabase.from('flights').delete().eq('id', createdFlight.id);
        }
        return { error: `Component hours update failed: ${hoursResult.error}` };
      }
    }

    return {
      flight: createdFlight,
      fuelOil: createdFuelOil,
      defects: createdDefects,
      expenses: createdExpenses,
    };
  } catch (err) {
    // Rollback on unexpected error
    for (const e of createdExpenses) {
      try { await supabase.from('flight_expenses').delete().eq('id', e.id); } catch (_) { /* best effort */ }
    }
    for (const d of createdDefects) {
      try { await supabase.from('defects').delete().eq('id', d.id); } catch (_) { /* best effort */ }
    }
    if (createdFuelOil) {
      try { await supabase.from('flight_fuel_oil').delete().eq('id', createdFuelOil.id); } catch (_) { /* best effort */ }
    }
    if (createdFlight) {
      try {
        if (isCompletingBooking) {
          // Revert status back to APPROVED
          await supabase.from('flights').update({ status: 'APPROVED' }).eq('id', createdFlight.id);
        } else {
          await supabase.from('flights').delete().eq('id', createdFlight.id);
        }
      } catch (_) { /* best effort */ }
    }
    return { error: err.message };
  }
}

